import { Link } from "react-router";
import type { EligibleSubsidy } from "@/features/subventions/types/subvention.types";
import { USE_ACTIONS, UNIVERSE_ICON } from "@/features/subventions/utils/universeActions";

type UniversePillsProps = { subsidy: EligibleSubsidy };

const PILL_BASE = "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold";

// "Où l'utiliser" — actionable universes link to where the subsidy is spent; others render as
// static pills. No universes → a neutral "Univers non précisé" pill.
export const UniversePills = ({ subsidy }: UniversePillsProps) => {
  const universes = (subsidy.universes ?? []).filter(Boolean);

  if (!universes.length) {
    return (
      <div className="flex flex-wrap gap-2">
        <span className={`${PILL_BASE} border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400`}>
          Univers non précisé
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {universes.map((universe) => {
        const icon = UNIVERSE_ICON[universe];
        const action = USE_ACTIONS[universe];
        // The icon is decorative: keep it out of the accessible name so a screen reader
        // announces the universe, not the emoji.
        const label = (
          <>
            {icon && <span aria-hidden>{icon}</span>}
            {universe}
          </>
        );

        if (!action) {
          return (
            <span
              key={universe}
              className={`${PILL_BASE} border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400`}
            >
              {label}
            </span>
          );
        }

        return (
          <Link
            key={universe}
            to={action.to}
            title={action.label}
            className={`${PILL_BASE} group border-gray-200 bg-gray-100 text-gray-600 transition-colors hover:border-primary hover:bg-lightprimary hover:text-primary-emphasis dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300`}
          >
            {label}
            <span className="ml-0.5 font-extrabold opacity-0 transition-opacity group-hover:opacity-100" aria-hidden>
              →
            </span>
          </Link>
        );
      })}
    </div>
  );
};

UniversePills.displayName = "UniversePills";
